import { ErrorBoundary as LocalErrorBoundary } from '@shared/components/shared/ErrorBoundary';
import { log } from './logger';

// Performance monitoring metrics
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, number> = new Map();
  private observers: Map<string, PerformanceObserver> = new Map();

  private constructor() {
    this.initializeObservers();
  }

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  private initializeObservers() {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      try {
        // Core Web Vitals
        const observer = new PerformanceObserver(list => {
          list.getEntries().forEach(entry => {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.trackMetric('page_load_time', navEntry.loadEventEnd - navEntry.startTime);
              this.trackMetric(
                'dom_content_loaded',
                navEntry.domContentLoadedEventEnd - navEntry.startTime
              );
              this.trackMetric('first_paint', navEntry.loadEventStart - navEntry.startTime);
            }

            if (entry.entryType === 'largest-contentful-paint') {
              this.trackMetric('largest_contentful_paint', entry.startTime);
            }

            if (entry.entryType === 'first-input') {
              const fidEntry = entry as PerformanceEventTiming;
              this.trackMetric('first_input_delay', fidEntry.processingStart - fidEntry.startTime);
            }

            if (entry.entryType === 'layout-shift') {
              const clsEntry = entry as any;
              if (!clsEntry.hadRecentInput) {
                this.trackMetric('cumulative_layout_shift', clsEntry.value);
              }
            }
          });
        });

        observer.observe({
          entryTypes: ['navigation', 'largest-contentful-paint', 'first-input', 'layout-shift'],
        });
        this.observers.set('core-web-vitals', observer);
      } catch (error) {
        log.warn('Failed to initialize performance observers', error);
      }
    }
  }

  public trackMetric(name: string, value: number) {
    this.metrics.set(name, value);
    log.performanceMetric(name, value);
  }

  public startTimer(name: string): () => void {
    const startTime = performance.now();
    return () => {
      const duration = performance.now() - startTime;
      this.trackMetric(name, duration);
    };
  }

  public getMetrics(): Record<string, number> {
    return Object.fromEntries(this.metrics);
  }

  public trackApiCall<T>(promise: Promise<T>, method: string, url: string): Promise<T> {
    const startTime = performance.now();
    const endTimer = this.startTimer(
      `api_call_${method.toLowerCase()}_${url.replace(/[^a-z0-9]/gi, '_')}`
    );

    return promise
      .then(result => {
        const duration = performance.now() - startTime;
        log.apiCall(method, url, 200, duration);
        endTimer();
        return result;
      })
      .catch(error => {
        const duration = performance.now() - startTime;
        const status = error?.response?.status || 500;
        log.apiCall(method, url, status, duration, error);
        endTimer();
        throw error;
      });
  }

  public destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.metrics.clear();
  }
}

// Error boundary integration
export interface ErrorBoundaryInfo {
  componentStack: string;
  errorBoundary?: string;
}

export const ErrorBoundary = LocalErrorBoundary;

// Export performance monitor instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Utility functions for manual error reporting
export const reportError = (error: Error, context?: Record<string, any>) => {
  log.error('Manual error report', error, context);
};

export const reportMessage = (
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, any>
) => {
  const logLevel = level === 'warning' ? 'warn' : level;
  (log as any)[logLevel](message, context);
};
